'use client'

import { clsx } from 'clsx'
import { WidgetInstance, type WidgetInstanceOptions } from 'friendly-challenge'
import { type FC, useEffect, useRef } from 'react'

import { useGlobalConfigContext } from '../../../global-config'

import { useSkipFriendlyCaptcha } from './useSkipFriendlyCaptcha'

import styles from './FriendlyCaptchaChallenge.module.css'

type FriendlyCaptchaChallengeProps = {
  className?: string
  doneCallback: WidgetInstanceOptions['doneCallback']
  startMode?: WidgetInstanceOptions['startMode']
  whenThisPropChangesResetCaptcha: unknown
}

export const FriendlyCaptchaChallenge: FC<FriendlyCaptchaChallengeProps> = ({
  className,
  doneCallback,
  startMode = 'focus',
  whenThisPropChangesResetCaptcha,
}) => {
  const captchaContainer = useRef<HTMLDivElement>(null)
  const widgetRef = useRef<WidgetInstance | null>(null)
  const { language } = useGlobalConfigContext()

  const doneCallbackRef = useRef(doneCallback)
  // if callback is not memoized use latest provided
  doneCallbackRef.current = doneCallback

  useEffect(() => {
    if (whenThisPropChangesResetCaptcha) {
      // reset if this run again and instance already there (e.g. in case error in form happened after server submission)
      widgetRef.current?.reset()
    }
  }, [whenThisPropChangesResetCaptcha])

  useEffect(() => {
    const root = captchaContainer.current

    if (!root || widgetRef.current) {
      return undefined
    }

    const container = document.createElement('div')

    root.append(container)

    const widget = new WidgetInstance(container, {
      doneCallback: (solution): void => {
        doneCallbackRef.current(solution)
      },
      language,
      puzzleEndpoint: 'https://eu-api.friendlycaptcha.eu/api/v1/puzzle',
      sitekey: 'FCMUL52LQ1I634TN',
      startMode,
    })

    widgetRef.current = widget

    return (): void => {
      widget.destroy()
      container.remove()
      widgetRef.current = null
    }
  }, [language, startMode])

  const skipFriendlyCaptcha = useSkipFriendlyCaptcha(doneCallbackRef)

  return skipFriendlyCaptcha ? (
    <div className="mb-4" />
  ) : (
    <div className={clsx(styles.frc, className)} ref={captchaContainer} />
  )
}
